import React, { useState, useMemo, useCallback } from "react";
import { useParams, Link, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Calendar, Users, MapPin, FileImage, Package, AlertTriangle, ShieldAlert, Edit, Layers, Pencil, Sliders, RefreshCw, UserCheck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiJson } from "@/lib/api";
import { campaignRebuildKeys } from "@/queries/campaignRebuildKeys";
import { apiRequest } from "@/lib/queryClient";
import { AssignmentsTab } from "./campaign-details/AssignmentsTab";
import CampaignHeader from "./campaign-details/CampaignHeader";
import { PostingInstructionsTab } from "@/components/admin/PostingInstructionsTab";
import { EditCampaignModal } from "@/components/admin/EditCampaignModal";
// Phase 3: DetailedCampaignEditModal removed - now using SnapshotEditWorkspace
import { SnapshotEditWorkspace } from "@/components/campaigns/SnapshotEditWorkspace";
import { RebuildCampaignModal } from "@/components/campaigns/RebuildCampaignModal";
import { OperatorAssignmentDriftModal } from "@/components/campaigns/OperatorAssignmentDriftModal";
import { FormatDiscrepanciesTab } from "@/components/FormatDiscrepanciesTab";
import { SitelistTab } from "./campaign-details/SitelistTab";
import { GroupsTab } from "./campaign-details/GroupsTab";
import { RetailerReportsTab } from "./campaign-details/RetailerReportsTab";

import { PageHeader, MetadataSeparator } from "@/components/PageHeader";
import { ParentContextBar, type ParentContextData } from "@/components/hierarchy/ParentContextBar";
import {
  appendListReturnParams,
  buildCampaignsListUrlFromReturnParams,
} from "@/lib/campaignsListUrl";

// Helper functions for campaign status display
const getStatusDisplayName = (status: string) => {
  const statusMap: Record<string, string> = {
    'draft': 'Draft',
    'confirmed': 'Confirmed',
    'artwork_received': 'Artwork Received',
    'artwork_approved': 'Artwork Approved',
    'printed': 'Printed',
    'posted': 'Posted',
    'removed': 'Removed',
    'active': 'Active',
    'completed': 'Completed'
  };
  return statusMap[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');
};

const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case 'draft':
      return 'outline';
    case 'confirmed':
    case 'artwork_received':
    case 'artwork_approved':
    case 'printed':
      return 'secondary';
    case 'posted':
    case 'active':
      return 'default';
    case 'removed':
    case 'completed':
      return 'destructive';
    default:
      return 'secondary';
  }
};

const getStatusDot = (status: string) => {
  switch (status) {
    case 'active':
    case 'posted':
      return 'bg-green-500';
    case 'draft':
      return 'bg-gray-500';
    case 'confirmed':
      return 'bg-blue-500';
    case 'removed':
    case 'completed':
      return 'bg-red-500';
    default:
      return 'bg-gray-500';
  }
};

const formatDate = (dateString: string) => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return 'Invalid Date';
  }
};

// Empty state component
const EmptyState = ({ title, description }: { title: string; description: string }) => (
  <div className="text-center py-8">
    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <AlertTriangle className="h-6 w-6 text-gray-400" />
    </div>
    <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-500">{description}</p>
  </div>
);

// (Cycle lock messaging removed for Campaign Details context)

// Error state component
const ErrorState = ({ title, description }: { title: string; description: string }) => (
  <div className="text-center py-8">
    <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <ShieldAlert className="h-6 w-6 text-red-500" />
    </div>
    <h3 className="text-lg font-medium text-red-900 mb-2">{title}</h3>
    <p className="text-sm text-red-600">{description}</p>
  </div>
);

// Loading state component
const LoadingState = ({ message = "Loading..." }: { message?: string }) => (
  <div className="flex items-center justify-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
    <span className="ml-2 text-muted-foreground">{message}</span>
  </div>
);

// STEP 4: Extract heavy UI subsections into memoized components

// Campaign header data type for the new PageHeader
interface CampaignHeaderData {
  name: string;
  status: string;
  statusBadgeVariant: "success" | "warning" | "info" | "secondary" | "destructive" | "default";
  retailerName: string;
  cycleDisplay: string;
  dateRange: string;
}

interface CampaignSummary {
  id: number;
  name: string;
  retailerName?: string | null;
  cycleId?: number | null;
  statusId?: number | null;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  campaignType?: string | null;
  campaign_type?: string | null;
  retailerId?: number | null;
  advertiserId?: number | null;
}

interface CampaignStatus {
  id: number;
  name: string;
}

interface DriftDetectionResult {
  inSync: boolean;
  totalAssignments: number;
  driftCount: number;
  driftedAssignments: Array<{
    storeId: number;
    storeName: string;
    mediaFormatId: number;
    mediaFormatName: string;
    plannedOperatorId: number;
    plannedOperatorName: string;
    currentOperatorId: number | null;
    currentOperatorName: string | null;
    source: "system" | "manual";
    diffType: "CHANGED" | "REMOVED" | "ADDED";
  }>;
}

interface CycleSnapshot {
  id: number;
  data?: {
    campaigns?: Array<{ id: number }>;
    packs?: Array<{ campaignId?: number | null }>;
  };
}

interface FullSnapshotResponse {
  data?: {
    campaigns?: Array<{ id: number; retailerName?: string | null }>;
    packs?: any[];
    totalStores?: number;
    totalOperators?: number;
  };
}

interface ReconcileResponse {
  appliedCount: number;
  skippedManualCount: number;
}

export default function CampaignDetails() {
  const { id } = useParams();
  const campaignId = parseInt(id || "0");
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  // Phase 3: Removed isDetailedEditModalOpen state - legacy modal no longer used
  const [isSnapshotWorkspaceOpen, setIsSnapshotWorkspaceOpen] = useState(false);
  const [isRebuildModalOpen, setIsRebuildModalOpen] = useState(false);
  const [isDriftModalOpen, setIsDriftModalOpen] = useState(false);
  const [headerData, setHeaderData] = useState<CampaignHeaderData | null>(null);
  const campaignsListBackUrl = useMemo(
    () => buildCampaignsListUrlFromReturnParams(window.location.search),
    [],
  );

  // Callback for CampaignHeader to pass data up for the PageHeader
  const handleHeaderDataLoaded = useCallback((data: CampaignHeaderData) => {
    setHeaderData(data);
  }, []);

  // STEP 5: Ensure all callbacks passed to children are stable
  const handleOpenQuickEdit = useCallback(() => setIsEditModalOpen(true), []);
  const handleCloseQuickEdit = useCallback(() => setIsEditModalOpen(false), []);
  // Phase 3: Removed handleOpenDetailedEdit and handleCloseDetailedEdit - legacy modal no longer used
  // Snapshot workspace handlers
  const handleOpenSnapshotWorkspace = useCallback(() => setIsSnapshotWorkspaceOpen(true), []);
  const handleCloseSnapshotWorkspace = useCallback(() => setIsSnapshotWorkspaceOpen(false), []);
  const handleEditSuccess = useCallback(() => {
    window.location.reload();
  }, []);

  // Rebuild Campaign handlers
  const handleOpenRebuildModal = useCallback(() => setIsRebuildModalOpen(true), []);
  const handleCloseRebuildModal = useCallback(() => setIsRebuildModalOpen(false), []);
  const handleConfirmRebuild = useCallback(() => {
    navigate(appendListReturnParams(`/campaigns/${campaignId}/rebuild`, window.location.search));
  }, [navigate, campaignId]);

  // Operator Assignment Drift handlers
  const handleOpenDriftModal = useCallback(() => setIsDriftModalOpen(true), []);
  const handleCloseDriftModal = useCallback(() => setIsDriftModalOpen(false), []);

  // Operator Assignment Reconciliation mutation
  const reconcileMutation = useMutation({
    mutationFn: async (selectedKeys: Array<{ storeId: number; mediaFormatId: number }>) => {
      const response = await apiRequest(
        `/api/campaigns/${campaignId}/operator-assignment-reconcile`,
        "POST",
        { selectedKeys },
      );
      return (await response.json()) as ReconcileResponse;
    },
    onSuccess: (data) => {
      toast({
        title: "Assignments updated",
        description: `Updated ${data.appliedCount} assignments. ${data.skippedManualCount > 0 ? `Skipped ${data.skippedManualCount} manual assignments.` : ''}`,
      });
      // Refetch drift data
      queryClient.invalidateQueries({ queryKey: ['/api/campaigns', campaignId, 'operator-assignment-drift'] });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error?.message || "Failed to update operator assignments",
        variant: "destructive",
      });
    },
  });

  const handleReconcile = useCallback(async (selectedKeys: Array<{ storeId: number; mediaFormatId: number }>) => {
    await reconcileMutation.mutateAsync(selectedKeys);
  }, [reconcileMutation]);

  // STEP 2: Stabilize helper functions
  const formatDateStable = useCallback((dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  }, []);

  const getStatusDisplayNameStable = useCallback((status: string) => {
    const statusMap: Record<string, string> = {
      'draft': 'Draft',
      'confirmed': 'Confirmed',
      'artwork_received': 'Artwork Received',
      'artwork_approved': 'Artwork Approved',
      'printed': 'Printed',
      'posted': 'Posted',
      'removed': 'Removed',
      'active': 'Active',
      'completed': 'Completed'
    };
    return statusMap[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown');
  }, []);

  const getStatusVariantStable = useCallback((status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'draft':
        return 'outline';
      case 'confirmed':
      case 'artwork_received':
      case 'artwork_approved':
      case 'printed':
        return 'secondary';
      case 'posted':
      case 'active':
        return 'default';
      case 'removed':
      case 'completed':
        return 'destructive';
      default:
        return 'secondary';
    }
  }, []);

  const getStatusDotStable = useCallback((status: string) => {
    switch (status) {
      case 'active':
      case 'posted':
        return 'bg-green-500';
      case 'draft':
        return 'bg-gray-500';
      case 'confirmed':
        return 'bg-blue-500';
      case 'removed':
      case 'completed':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }, []);

  // Auto-open edit modal if redirected from old edit route
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('edit') === 'true') {
      setIsEditModalOpen(true);
      // Clean up URL
      window.history.replaceState({}, '', `/campaigns/${campaignId}`);
    }
  }, [campaignId]);

  // Fetch campaign details
  const { data: campaign, isLoading: campaignLoading, error: campaignError } = useQuery<CampaignSummary | null>({
    queryKey: ["/api/campaigns", campaignId],
    queryFn: async () => {
      try {
        const response = await apiJson<CampaignSummary>(`/api/campaigns/${campaignId}`);
        return response;
      } catch (error: any) {
        if (error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!campaignId,
  });

  // Extract cycleId from campaign
  const cycleId = campaign?.cycleId;

  // Fetch campaign statuses early — needed by isDraft and the status dropdown
  const { data: campaignStatuses = [] } = useQuery<CampaignStatus[]>({
    queryKey: ["/api/campaign-statuses"],
    queryFn: () => apiJson<CampaignStatus[]>('/api/campaign-statuses'),
  });

  // STEP 1: Memoize isDraft computation
  // Check statusId, legacy status text, AND resolve status name from campaignStatuses lookup
  const isDraft = useMemo(() => {
    const statusId = Number(campaign?.statusId ?? (campaign as any)?.status_id);
    if (statusId === 1) return true;
    if (String(campaign?.status ?? '').toLowerCase() === 'draft') return true;
    // Fallback: resolve status name from campaignStatuses table using statusId
    if (statusId && campaignStatuses.length > 0) {
      const resolved = campaignStatuses.find((s: any) => s.id === statusId);
      if (resolved && String(resolved.name).toLowerCase() === 'draft') return true;
    }
    return false;
  }, [campaign, campaignStatuses]);

  // Check if campaign is eligible for Rebuild via server-side check
  // This fetches rebuild-data to get authoritative eligibility (checks locked artefacts, etc.)
  const { data: rebuildEligibility, isLoading: rebuildEligibilityLoading } = useQuery<{ isEligible: boolean; reason?: string }>({
    queryKey: campaignRebuildKeys.rebuildEligibility(campaignId),
    queryFn: async () => {
      try {
        const response = await apiJson<{ isEligible?: boolean; eligibilityReason?: string }>(`/api/campaigns/${campaignId}/rebuild-data`);
        return { isEligible: response.isEligible === true, reason: response.eligibilityReason };
      } catch {
        return { isEligible: false, reason: 'Failed to check eligibility' };
      }
    },
    enabled: !!campaignId,
    staleTime: 30000, // Cache for 30 seconds
  });

  const isRebuildEligible = rebuildEligibility?.isEligible === true;
  const rebuildUnavailableReason = rebuildEligibilityLoading
    ? 'Checking whether this campaign can be rebuilt...'
    : rebuildEligibility?.reason || 'This campaign cannot be rebuilt in its current state.';

  // Operator Assignment Drift detection
  const { data: driftData, isLoading: driftLoading, error: driftError } = useQuery<DriftDetectionResult>({
    queryKey: ['/api/campaigns', campaignId, 'operator-assignment-drift'],
    queryFn: async () => {
      const response = await apiJson<DriftDetectionResult>(`/api/campaigns/${campaignId}/operator-assignment-drift`);
      return response;
    },
    enabled: !!campaignId && isDriftModalOpen, // Only fetch when modal is opened
    staleTime: 60000, // Cache for 60 seconds
  });

  // Get campaign type for tooltip
  const campaignType = useMemo(() => {
    return (campaign as any)?.campaignType ?? (campaign as any)?.campaign_type ?? 'unknown';
  }, [campaign]);

  // Step 1: Fetch cycle snapshots to find the one containing our campaign
  const { data: snapshots, isLoading: snapshotsLoading, error: snapshotsError } = useQuery<CycleSnapshot[] | null>({
    queryKey: ["cycle-snapshots", cycleId],
    queryFn: async () => {
      try {
        const response = await apiJson<CycleSnapshot[]>(`/api/cycles/${cycleId}/snapshots`);
        return response;
      } catch (error: any) {
        if (error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!cycleId,
  });

  // Find the snapshot that contains our campaign
  const snapshotId = useMemo(() =>
    snapshots?.find((s: any) => {
      // Server now returns a compact campaignIds array (WI-2) instead of the
      // full `data` blob. Fall back to the legacy data.campaigns/packs shape for
      // any cached/older responses that still include it.
      if (Array.isArray(s?.campaignIds)) return s.campaignIds.includes(campaignId);
      return (
        s.data?.campaigns?.some((c: any) => c.id === campaignId) ||
        s.data?.packs?.some((p: any) => p.campaignId === campaignId)
      );
    })?.id,
    [snapshots, campaignId]
  );

  // Step 2: Fetch the full snapshot data using the snapshotId
  const { data: fullSnapshot, isLoading: snapshotLoading, error: snapshotError } = useQuery<FullSnapshotResponse | null>({
    queryKey: ["campaign-snapshot", snapshotId, campaignId],  // Include campaignId to prevent cache collision
    queryFn: async () => {
      try {
        const response = await apiJson<FullSnapshotResponse>(`/api/cycle-snapshots/${snapshotId}`);
        return response;
      } catch (error: any) {
        if (error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!snapshotId,
  });

  // Snapshot loading state
  const isDataLoading = snapshotLoading || snapshotsLoading;

  // Phase 4.7A: Fetch campaign summary for parent context bar
  const { data: campaignSummary } = useQuery<{
    clientId?: number | null;
    clientName?: string | null;
    advertiserId?: number | null;
    advertiserName?: string | null;
    bookingId?: number | null;
    bookingName?: string | null;
    name?: string | null;
  }>({
    queryKey: ["/api/campaigns", campaignId, "summary"],
    queryFn: () => apiJson(`/api/campaigns/${campaignId}/summary`),
    enabled: !!campaignId,
    staleTime: 30_000,
  });

  // Compute parent context data for ParentContextBar
  const parentContextData = useMemo((): ParentContextData => ({
    clientId: campaignSummary?.clientId,
    clientName: campaignSummary?.clientName,
    advertiserId: (campaign as any)?.advertiserId ?? campaignSummary?.advertiserId,
    advertiserName: campaignSummary?.advertiserName,
    bookingId: campaignSummary?.bookingId,
    bookingName: campaignSummary?.bookingName,
    campaignName: campaignSummary?.name || (campaign as any)?.name || "Campaign",
  }), [campaign, campaignSummary]);

  // STEP 3: Convert campaignStatuses to Map for O(1) lookups
  const campaignStatusMap = useMemo(() => {
    return new Map(campaignStatuses.map((s: any) => [s.id, s]));
  }, [campaignStatuses]);

  // Debug logging for status selection
  React.useEffect(() => {
    if (campaign && campaignStatuses.length > 0) {
      console.log('[STATUS_DEBUG] Campaign status:', {
        campaignStatus: campaign.status,
        campaignStatusId: campaign.statusId,
        isDraft,
        availableStatuses: campaignStatuses.map(s => ({ id: s.id, name: s.name }))
      });
    }
  }, [campaign, campaignStatuses, isDraft]);

  // Mutation for updating campaign status
  const updateStatusMutation = useMutation({
    mutationFn: (statusId: number) => {
      console.log('[MUTATION] ===== MUTATION START =====');
      console.log('[MUTATION] Campaign ID:', campaignId);
      console.log('[MUTATION] Status ID:', statusId);
      const url = `/api/campaigns/${campaignId}/status`;
      console.log('[MUTATION] Calling API:', url, 'with statusId:', statusId);
      return apiRequest(url, 'PATCH', { statusId });
    },
    onMutate: async (statusId) => {
      console.log('[MUTATION] onMutate called with statusId:', statusId);
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/campaigns", campaignId] });

      // Snapshot the previous value
      const previousCampaign = queryClient.getQueryData(["/api/campaigns", campaignId]);
      console.log('[MUTATION] Previous campaign data:', previousCampaign);

      // Optimistically update the cache
      queryClient.setQueryData(["/api/campaigns", campaignId], (old: any) => {
        if (!old) return old;
        // STEP 3: Use campaignStatusMap for O(1) lookup
        const newStatus = campaignStatusMap.get(statusId);
        console.log('[MUTATION] New status found:', newStatus);
        const updated = {
          ...old,
          statusId: statusId,
          status: newStatus?.name || old.status,
        };
        console.log('[MUTATION] Updated campaign data:', updated);
        return updated;
      });

      return { previousCampaign };
    },
    onSuccess: (data) => {
      console.log('[MUTATION] ✅ onSuccess called with data:', data);
      console.log('[MUTATION] Cache before invalidation:', queryClient.getQueryData(["/api/campaigns", campaignId]));

      toast({
        title: "Status Updated",
        description: "Campaign status has been updated successfully.",
      });

      // Refetch to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ["/api/campaigns", campaignId] });

      // Log after invalidation to see the updated data
      setTimeout(() => {
        const updatedData = queryClient.getQueryData(["/api/campaigns", campaignId]);
        console.log('[MUTATION] Cache after invalidation:', updatedData);
      }, 100);
    },
    onError: (error, statusId, context) => {
      console.log('[MUTATION] onError called with error:', error, 'statusId:', statusId);
      // Revert the optimistic update on error
      if (context?.previousCampaign) {
        queryClient.setQueryData(["/api/campaigns", campaignId], context.previousCampaign);
      }
      toast({
        title: "Error",
        description: "Failed to update campaign status. Please try again.",
        variant: "destructive",
      });
    },
  });

  // STEP 1: Memoize all heavy derived structures
  const snapshotData = useMemo(() => fullSnapshot?.data, [fullSnapshot]);

  const campaignInfo = useMemo(() =>
    snapshotData?.campaigns?.find((c: any) => c.id === campaignId),
    [snapshotData, campaignId]
  );

  // CRITICAL FIX: Only use packs that belong to THIS campaign
  // Filter packs by campaignId to prevent showing data from other campaigns
  const campaignPacks = useMemo(() =>
    (snapshotData?.packs || []).filter((pack: any) => pack.campaignId === campaignId),
    [snapshotData, campaignId]
  );

  // Process packs data for media summary
  const mediaSummary = useMemo(() =>
    campaignPacks.map((pack: any) => ({
      packId: pack.id,
      packName: pack.name,
      formats: pack.storeFormatAssignments?.map((assignment: any) => ({
        formatId: assignment.mediaFormatId,
        formatName: assignment.mediaFormatName,
        plannedUnits: assignment.quantity,
        storeCount: 1 // Each assignment represents one store
      })) || pack.formats || []
    })),
    [campaignPacks]
  );

  // Process assignments data (handle both storeOperatorAssignments and storeFormatAssignments)
  const storeOperatorSummary = useMemo(() =>
    campaignPacks.flatMap((pack: any) => {
      const assignments = pack.storeOperatorAssignments || pack.storeFormatAssignments || [];
      return assignments.map((assignment: any) => ({
        storeId: assignment.storeId,
        storeName: assignment.storeName,
        operatorId: assignment.operatorId,
        operatorName: assignment.operatorName,
        mediaFormatName: assignment.mediaFormatName,
        quantity: assignment.quantity,
        packName: pack.packName || pack.name
      }));
    }),
    [campaignPacks]
  );

  const assignments = useMemo(() =>
    Array.isArray(storeOperatorSummary) ? storeOperatorSummary : [],
    [storeOperatorSummary]
  );

  // Format discrepancies are now fetched from API endpoint, not from snapshot
  // (Snapshots are immutable reference points - discrepancies are calculated on-demand)

  // Use backend-calculated summary stats from snapshot data
  const totalStores = useMemo(() => snapshotData?.totalStores || 0, [snapshotData]);
  const totalOperators = useMemo(() => snapshotData?.totalOperators || 0, [snapshotData]);
  const totalFormats = useMemo(() =>
    mediaSummary.reduce((sum: number, pack: any) => sum + (pack.formats?.length || 0), 0),
    [mediaSummary]
  );

  // Get retailer info from campaign or snapshot
  const retailerInfo = useMemo(() =>
    campaignInfo?.retailerName || campaign?.retailerName || 'Unknown',
    [campaignInfo, campaign]
  );

  // Get date range safely
  const dateRange = useMemo(() =>
    campaign?.startDate && campaign?.endDate ?
      `${formatDateStable(campaign.startDate)} - ${formatDateStable(campaign.endDate)}` :
      'Not specified',
    [campaign, formatDateStable]
  );

  // Get cycles safely
  const cycleInfo = useMemo(() =>
    campaign?.cycleId ? `Cycle ${campaign.cycleId}` : 'No cycle assigned',
    [campaign]
  );

  // Handle loading and error states after all hooks and computations
  if (campaignLoading) {
    return <LoadingState message="Loading campaign details..." />;
  }

  if (campaignError) {
    return (
      <div className="p-6">
        <ErrorState
          title="Error Loading Campaign"
          description="There was an error loading the campaign details. Please try again."
        />
      </div>
    );
  }

  if (campaign === null) {
    return (
      <div className="p-6">
        <EmptyState
          title="Campaign Not Found"
          description="The requested campaign could not be found."
        />
        <div className="mt-4 text-center">
          <Link href={campaignsListBackUrl}>
            <Button variant="outline">Back to Campaigns</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return <LoadingState />;
  }

  return (
    <div className="p-6 space-y-6 bg-muted/30 min-h-screen">
      {/* Phase 4.7A: Parent Context Bar - shows Client → Advertiser → Booking → Campaign hierarchy */}
      <ParentContextBar data={parentContextData} className="-mx-6 -mt-6 mb-4" />

      {/* PageHeader with metadata and actions */}
      <PageHeader
        title={headerData?.name || campaign?.name || "Untitled Campaign"}
        metadata={
          headerData ? (
            <>
              <Badge variant={headerData.statusBadgeVariant}>
                {headerData.status.charAt(0).toUpperCase() + headerData.status.slice(1)}
              </Badge>
              <MetadataSeparator />
              <span>{headerData.retailerName}</span>
              <MetadataSeparator />
              <span>Cycle: {headerData.cycleDisplay} – {headerData.dateRange}</span>
            </>
          ) : null
        }
        actions={
          <>
            <Link href={campaignsListBackUrl}>
              <Button variant="ghost" size="sm" className="h-9">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Campaigns
              </Button>
            </Link>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    className="h-9"
                    onClick={handleOpenQuickEdit}
                    disabled={!isDraft}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Quick Edit
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1">
                    <p className="font-semibold">Quick edit</p>
                    <p className="text-xs">{isDraft ? 'Make small, safe changes like names or notes. Does not change stores, formats, operators, or quantities.' : 'Only draft campaigns can be edited. Change status to Draft first.'}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Phase 2: Single "Detailed Edit" button - uses SnapshotEditWorkspace */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="h-9"
                    onClick={handleOpenSnapshotWorkspace}
                    disabled={!isDraft}
                  >
                    <Sliders className="h-4 w-4 mr-1" />
                    Detailed Edit
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1">
                    <p className="font-semibold">Detailed edit</p>
                    <p className="text-xs">{isDraft ? 'Manually edit the campaign setup — stores, formats, quantities, and operators. Use this when you need to fix specific rows.' : 'Only draft campaigns can be edited. Change status to Draft first.'}</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Restructure button - always visible, disabled with reason when unavailable */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9"
                      onClick={handleOpenRebuildModal}
                      disabled={!isRebuildEligible}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Restructure
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1">
                    <p className="font-semibold">Restructure campaign</p>
                    <p className="text-xs">
                      {isRebuildEligible
                        ? 'Open the group-based structure editor to add, split, or reorganise stores and formats before saving a new snapshot.'
                        : rebuildUnavailableReason}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Check Operator Assignments button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9"
                    onClick={handleOpenDriftModal}
                  >
                    <UserCheck className="h-4 w-4 mr-1" />
                    Check Operator Assignments
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1">
                    <p className="font-semibold">Check operator assignments</p>
                    <p className="text-xs">
                      See whether operator assignments have changed since this campaign was created.
                      {isDraft ? ' Draft campaigns can update selected rows.' : ' Read-only.'}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        }
      />

      {/* Edit Campaign Modal (Quick Edit) */}
      <EditCampaignModal
        campaignId={campaignId}
        isOpen={isEditModalOpen}
        onClose={handleCloseQuickEdit}
        onSuccess={handleEditSuccess}
      />

      {/* Phase 3: Legacy DetailedCampaignEditModal removed - SnapshotEditWorkspace is now the sole detailed editor */}
      <SnapshotEditWorkspace
        campaignId={campaignId}
        isOpen={isSnapshotWorkspaceOpen}
        onClose={handleCloseSnapshotWorkspace}
        onSuccess={handleEditSuccess}
      />

      {/* Restructure Campaign Modal */}
      <RebuildCampaignModal
        isOpen={isRebuildModalOpen}
        onClose={handleCloseRebuildModal}
        onConfirm={handleConfirmRebuild}
        campaignName={campaign?.name || 'Campaign'}
      />

      {/* Operator Assignment Drift Modal */}
      <OperatorAssignmentDriftModal
        open={isDriftModalOpen}
        onOpenChange={setIsDriftModalOpen}
        driftData={driftData}
        isLoading={driftLoading}
        error={driftError}
        campaignId={campaignId}
        isDraft={isDraft}
        onReconcile={handleReconcile}
        isReconciling={reconcileMutation.isPending}
      />

      {/* Basic Details Card - hidden header, used for data loading */}
      <CampaignHeader campaignId={campaignId} onDataLoaded={handleHeaderDataLoaded} />

      {/* Tabs */}
      <Tabs defaultValue="groups" className="space-y-4">
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="groups">Groups</TabsTrigger>
          <TabsTrigger value="sitelist">Sitelist</TabsTrigger>
          <TabsTrigger value="discrepancies">
            Delivery & Compliance (mobile)
          </TabsTrigger>
          <TabsTrigger value="assignments">Assignments</TabsTrigger>
          <TabsTrigger value="instructions">Posting Instructions</TabsTrigger>
          <TabsTrigger value="retailer-reports">Retailer Reports</TabsTrigger>
        </TabsList>

        {/* Groups Tab (Phase 0 - Unified Groups View) */}
        <TabsContent value="groups">
          <GroupsTab campaignId={campaignId} />
        </TabsContent>

        {/* Sitelist Tab */}
        <TabsContent value="sitelist">
          <SitelistTab campaignId={campaignId} />
        </TabsContent>

        {/* Format Discrepancies Tab */}
        <TabsContent value="discrepancies">
          <FormatDiscrepanciesTab campaignId={campaignId} />
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments">
          <AssignmentsTab campaignId={campaignId} />
        </TabsContent>

        {/* Posting Instructions Tab */}
        <TabsContent value="instructions">
          <Card>
            <CardHeader>
              <CardTitle>Posting Instructions</CardTitle>
              <p className="text-sm text-muted-foreground">
                {isDraft
                  ? 'Enter posting instructions for operators. These will be frozen at lock time.'
                  : 'Posting instructions for this campaign (read-only).'}
              </p>
            </CardHeader>
            <CardContent>
              {(() => {
                const retailerId = (campaign as any)?.retailerId || 0;
                const campaignCycleId = cycleId || 0;
                const campaignTypeValue = (campaign as any)?.campaignType || (campaign as any)?.campaign_type;
                return (
                  <PostingInstructionsTab
                    campaign={{ 
                      id: campaignId, 
                      name: campaign.name, 
                      retailerId, 
                      cycleId: campaignCycleId,
                      campaignType: campaignTypeValue,
                    }}
                    readOnly={!isDraft}
                  />
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retailer Reports Tab */}
        <TabsContent value="retailer-reports">
          <RetailerReportsTab campaignId={campaignId} cycleId={cycleId} />
        </TabsContent>

      </Tabs>
    </div >
  );
}
